import type { ReactNode } from 'react';
import { ArrowLeft, ArrowRight, CircleNotch } from '@phosphor-icons/react';
import { AppLink } from '../../lib/app-navigation';
import { Skeleton } from '../shared/Skeleton';
import './AuthPage.css';

type Language = 'zh' | 'en';

// The route fallback and live form share the same shell, so loading does not
// replace the page with a differently sized, full-width skeleton.
export function AuthLayout({ language, returnHref, children }: { language: Language; returnHref?: string; children: ReactNode }) {
  const tr = (zh: string, en: string) => language === 'zh' ? zh : en;
  const brand = <AppLink className="halo-auth-brand" to="/" aria-label={tr('Halo 首页', 'Halo home')}>Halo</AppLink>;
  return <div className="halo-auth-page" lang={language === 'zh' ? 'zh-CN' : 'en'}>
    <aside className="halo-auth-story" aria-label={tr('关于 Halo', 'About Halo')}>
      {brand}
      <div className="halo-auth-story-copy">
        <p className="halo-auth-story-title">{tr('加拿大移民', 'Canadian immigration')}<br />{tr('资料与咨询', 'Documents & advice')}</p>
        <p className="halo-auth-story-description">{tr('CRS 计算 · 资料评估 · 顾问咨询', 'CRS calculator · Document assessment · Consultations')}</p>
      </div>
    </aside>
    <div className="halo-auth-panel">
      <header className="halo-auth-header"><div className="halo-auth-mobile-brand">{brand}</div>{returnHref && <AppLink className="halo-auth-return" to={returnHref}><ArrowLeft size={16} aria-hidden="true" />{tr('返回当前任务', 'Back to your task')}</AppLink>}</header>
      <main id="main-content" className="halo-auth-main" aria-labelledby="halo-auth-heading">{children}<AppLink className="halo-auth-anonymous" to="/assessment">{tr('先试试 CRS 计算器', 'Try the CRS calculator')}<ArrowRight size={16} aria-hidden="true" /></AppLink></main>
      <footer className="halo-auth-footer"><nav aria-label={tr('网站说明', 'Site information')}><AppLink to="/privacy">{tr('隐私', 'Privacy')}</AppLink><AppLink to="/terms">{tr('条款', 'Terms')}</AppLink><AppLink to="/data-controls">{tr('数据管理', 'Data controls')}</AppLink></nav><span>© {new Date().getFullYear()} Halo</span></footer>
    </div>
  </div>;
}

export function AuthLoading({ language }: { language: Language }) {
  return <div className="halo-auth-loading">
    <div className="halo-auth-loading-shape" aria-hidden="true"><Skeleton height={48} /><div className="halo-auth-loading-field"><Skeleton width="25%" height={14} /><Skeleton height={52} /></div><Skeleton height={38} /><Skeleton height={52} /></div>
    <p className="halo-auth-progress" role="status"><CircleNotch size={18} aria-hidden="true" />{language === 'zh' ? '正在连接登录服务…' : 'Connecting to sign-in…'}</p>
  </div>;
}

export function AuthPageFallback({ language }: { language: Language }) {
  return <AuthLayout language={language}><header className="halo-auth-heading"><h1 id="halo-auth-heading">{language === 'zh' ? '登录 Halo' : 'Sign in to Halo'}</h1></header><AuthLoading language={language} /></AuthLayout>;
}
